import {
  HHBox,
  HHGrid,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { FILE_PROCESSING_STATUS_LABELS } from '../constants/partner-data-gateway.constant';
import { ControlAction } from '../domain/enums';
import { ControlResult, IngestionDetail } from '../domain/types';
import { printDateAndTime } from '../utils/print-date-and-time';
import { CopyToClipboard } from './copy-to-clipboard';
import { IngestionControlActions } from './ingestion-control-actions';
import { StatusChip } from './status-chip';

export const INGESTION_HEADER_TEST_ID = 'ingestion-header';
export const INGESTION_HEADER_FILE_NAME_TEST_ID = 'ingestion-header-file-name';
export const INGESTION_HEADER_STATUS_CHIP_TEST_ID =
  'ingestion-header-status-chip';
export const INGESTION_HEADER_EVENT_ID_TEST_ID = 'ingestion-header-event-id';
export const INGESTION_HEADER_STATUS_LIVE_REGION_TEST_ID =
  'ingestion-header-status-live-region';

interface IngestionHeaderProps {
  ingestion: IngestionDetail;
  isControlling: boolean;
  controlIngestion: (action: ControlAction) => Promise<ControlResult>;
}

export const IngestionHeader = ({
  ingestion,
  isControlling,
  controlIngestion,
}: IngestionHeaderProps): JSX.Element => (
  <HHBox data-testid={INGESTION_HEADER_TEST_ID}>
    <HHGrid
      container
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
    >
      <HHGrid item>
        <HHStack direction="row" spacing={2} alignItems="center">
          {/*
            The page polls every 2 s and the status moves through several
            phases; announce each change without needing focus (WCAG 4.1.3).
            Only here, not on table chips, where it would chatter per row.
          */}
          <HHBox
            component="span"
            role="status"
            aria-live="polite"
            data-testid={INGESTION_HEADER_STATUS_LIVE_REGION_TEST_ID}
          >
            <StatusChip
              status={ingestion.status}
              label={FILE_PROCESSING_STATUS_LABELS[ingestion.status]}
              testId={INGESTION_HEADER_STATUS_CHIP_TEST_ID}
            />
          </HHBox>
          <HHTypography
            hhVariant="variant-bypass"
            variant="h5"
            component="h1"
            data-testid={INGESTION_HEADER_FILE_NAME_TEST_ID}
            sx={{ fontSize: 20, fontWeight: 600, wordBreak: 'break-all' }}
          >
            {ingestion.fileName}
          </HHTypography>
        </HHStack>
      </HHGrid>
      <HHGrid item>
        <IngestionControlActions
          fileName={ingestion.fileName}
          status={ingestion.status}
          isControlling={isControlling}
          controlIngestion={controlIngestion}
        />
      </HHGrid>
    </HHGrid>
    <HHStack
      direction="row"
      spacing={4}
      flexWrap="wrap"
      alignItems="center"
      marginTop={2}
    >
      <HHStack
        direction="row"
        spacing={1}
        alignItems="center"
        data-testid={INGESTION_HEADER_EVENT_ID_TEST_ID}
      >
        <HHTypography hhVariant="body2" color="textSecondary">
          Event ID: {ingestion.eventId}
        </HHTypography>
        <CopyToClipboard
          data={ingestion.eventId}
          label="Copy event ID to clipboard"
        />
      </HHStack>
      <HHTypography hhVariant="body2" color="textSecondary">
        Provider: {ingestion.dataProvider ?? 'Unknown'}
      </HHTypography>
    </HHStack>
    <HHStack direction="row" spacing={4} flexWrap="wrap" marginTop={1}>
      <HHTypography hhVariant="body2" color="textSecondary">
        {/* PDGS exposes the object location as bucket + key, not one path. */}
        S3:{' '}
        {ingestion.bucket
          ? `${ingestion.bucket}/${ingestion.s3Key}`
          : ingestion.s3Key}
      </HHTypography>
      <HHTypography hhVariant="body2" color="textSecondary">
        Created: {printDateAndTime(ingestion.createdAt)}
      </HHTypography>
    </HHStack>
  </HHBox>
);
